import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService, MockDataService, AuthService } from '../../core/services';
import { clearCache } from '../../core/interceptors/cache.interceptor-fn';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-dev-tools',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './dev-tools.component.html',
  styleUrl: './dev-tools.component.scss'
})
export class DevToolsComponent {
  isOpen = false;
  simulateMainApp = false;
  mockUserInfo: any = null;

  constructor(
    public languageService: LanguageService,
    private mockDataService: MockDataService,
    private authService: AuthService
  ) {
    this.simulateMainApp = this.mockDataService.isSimulationEnabled();
    this.mockUserInfo = this.mockDataService.getMockUserInfo();
  }

  /**
   * Check if dev tools should be visible
   * Only show in development and local environments
   * Hidden in UAT and Production
   */
  get isDevMode(): boolean {
    return environment.development === true;
  }

  togglePanel(): void {
    this.isOpen = !this.isOpen;
  }

  /**
   * Toggle main app simulation
   * When enabled: Simulates ReactNativeWebView sending user info
   * When disabled: Expects real main app to send user info
   * 
   * IMPORTANT: Clears all caches to prevent stale data from persisting
   */
  toggleSimulation(): void {
    this.simulateMainApp = this.mockDataService.toggleSimulation();
    
    // Clear sessionStorage to remove stale user data
    this.authService.clearSession();
    
    // Clear all user-related cookies
    this.clearUserCookies();
    
    // Clear localStorage (except language preference)
    this.clearUserLocalStorage();
    
    // Clear HTTP cache (API responses)
    clearCache();
    //console.log('🗑️ All caches cleared on simulation toggle');
    
    // Reload page to apply changes
    window.location.reload();
  }

  /**
   * Clear all user-related cookies
   */
  private clearUserCookies(): void {
    const cookiesToClear = ['ih_phone', 'ih_userid', 'ih_otp', 'ih_lang'];
    cookiesToClear.forEach(cookieName => {
      // Set cookie with past expiration date to delete it
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  }

  /**
   * Clear user data from localStorage (preserve language setting)
   */
  private clearUserLocalStorage(): void {
    const keysToPreserve = ['insee_language', 'insee_simulate_main_app'];
    const allKeys = Object.keys(localStorage);
    
    allKeys.forEach(key => {
      if (!keysToPreserve.includes(key)) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Switch language and reload page
   * @param lang Language code ('en' or 'lo')
   */
  switchLanguage(lang: 'en' | 'lo'): void {
    this.languageService.setLanguage(lang);
    // Reload page to apply language changes
    window.location.reload();
  }

  /**
   * Check if currently running inside WebView
   */
  get isInWebView(): boolean {
    return this.mockDataService.isInWebView();
  }
}
